import { AgCharts } from 'ag-charts-react';
import { colours } from '../../../StyleConstants';
import React, { useMemo, useRef } from 'react';
import "ag-charts-enterprise";
import { AgBarSeriesItemStylerParams } from "ag-charts-enterprise";
import { AgChartOptions } from "ag-charts-community";
import { AgGridReact } from "@ag-grid-community/react";
import ChartPaper, {
    AXIS_LABEL_FONT_SIZE,
    CHART_HEIGHT,
    CHART_PADDING,
    estimateTableHeight,
    TABLE_HEADER_HEIGHT,
    TABLE_ROW_HEIGHT,
    TOOLTIP_PADDING,
    truncateLabel,
    Y_AXIS_SEGMENTS,
    Y_AXIS_STEP_ROUNDING
} from "./ChartPaper";
import { Stack } from "@mui/material";
import { SalesforceIssueRecord } from "../../../api/issueApiSlice";

type BarPoint = { label: string; value: number; description?: string };

// Hoisted so AG Grid does not re-apply its columns on every render of the dashlet.
const TABLE_COLUMN_DEFS = [{ field: 'label' }, { field: 'value' }];
const TABLE_AUTO_SIZE_STRATEGY = { type: 'fitGridWidth' } as const;

type Props = {
    data: BarPoint[];
    title: string;
    source?: string;
    ddAvailable: boolean;
    imagingData: any;
    eegData?: any;
    selectedSessionFilter?: string[];
    sessionInfoMap?: any;
    cohort?: boolean;
    showDescriptionOnHover?: boolean;
    subtitle?: string;
    breakdownAvailable?: boolean;
    breakdownClicked?: (field: string) => void;
    table_name?: string;
    getBarFill?: (data: any, defaultFill: any) => string;
    partialOptions?: Partial<AgChartOptions>;
    showAsTable?: boolean;
    showIssueOptions?: boolean;
    salesforceRecordsByInstrument?: Record<string, SalesforceIssueRecord[]>;
    instrumentDomainRelation?: Record<string, string>;
};

const BarChart = ({
    data,
    title,
    ddAvailable,
    imagingData,
    selectedSessionFilter,
    eegData,
    cohort,
    subtitle,
    table_name,
    breakdownAvailable,
    breakdownClicked,
    getBarFill,
    partialOptions,
    showAsTable,
    showIssueOptions,
    salesforceRecordsByInstrument,
    instrumentDomainRelation
}: Props) => {
    const gridRef = useRef<AgGridReact>(null);

    const maxNumber = useMemo(
        () => Math.max(...data.map((item) => item.value)),
        [data]
    );

    const sessionFilteredRows = useMemo(() => {
        if (!selectedSessionFilter) return data;
        const set = new Set(selectedSessionFilter);
        return data.filter((row) => set.has(row.label));
    }, [data, selectedSessionFilter]);

    const escapeHtml = (s: unknown) =>
        String(s ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

    return (
        <ChartPaper
            title={title}
            subtitle={subtitle}
            table_name={table_name}
            breakdownAvailable={breakdownAvailable}
            breakdownClicked={breakdownClicked}
            ddAvailable={ddAvailable}
            imagingData={imagingData}
            eegData={eegData}
            showIssueOptions={showIssueOptions}
            tableRows={sessionFilteredRows}
            salesforceRecordsByInstrument={salesforceRecordsByInstrument}
            instrumentDomainRelation={instrumentDomainRelation}
        >
            {data.length > 1000
                ? <Stack flexGrow={1} fontWeight={600}>SKIPPED due to large data size</Stack>
                : showAsTable ?? false ?
                    (<div
                        className={"ag-theme-alpine"}
                        style={{
                            width: '100%',
                            display: "block",
                            minHeight: `${estimateTableHeight(sessionFilteredRows.length)}px`,
                        }}
                    >
                        <AgGridReact
                            ref={gridRef}
                            key={title}
                            rowData={sessionFilteredRows}
                            columnDefs={TABLE_COLUMN_DEFS}
                            domLayout={'autoHeight'}
                            rowHeight={TABLE_ROW_HEIGHT}
                            headerHeight={TABLE_HEADER_HEIGHT}
                            suppressAnimationFrame={true}
                            autoSizeStrategy={TABLE_AUTO_SIZE_STRATEGY}
                            suppressDragLeaveHidesColumns = {true}
                        />
                    </div>)
                    : <AgCharts
                        style={{ height: `${CHART_HEIGHT}px`, contain: 'size layout' }}
                        key={title}
                        options={{
                            ...partialOptions,
                            data: sessionFilteredRows,
                            animation: { enabled: true },
                            padding: CHART_PADDING,

                            axes: [
                                {
                                    type: "category",
                                    position: "bottom",
                                    label: {
                                        fontSize: AXIS_LABEL_FONT_SIZE,
                                        autoRotate: true,
                                        avoidCollisions: true,
                                        wrapping: cohort ? 'hyphenate' : 'on-space',
                                        formatter: ({ value }) => truncateLabel(String(value ?? "")),
                                    },
                                },
                                {
                                    type: "number",
                                    position: "left",
                                    label: { fontSize: AXIS_LABEL_FONT_SIZE },
                                    interval: { step: Math.round((maxNumber / Y_AXIS_SEGMENTS) / Y_AXIS_STEP_ROUNDING) * Y_AXIS_STEP_ROUNDING },
                                },
                            ],

                            series: [
                                {
                                    type: "bar",
                                    xKey: "label",
                                    yKey: "value",
                                    yName: "",
                                    tooltip: {
                                        range: 50,
                                        renderer: ({ datum }: {datum: any}) => {
                                            const variableName = datum?.label ?? "";
                                            const value = datum?.value ?? "";
                                            const instrumentName = datum?.description;

                                            const hasInstrument =
                                                typeof instrumentName === 'string'
                                                    ? instrumentName.trim().length > 0
                                                    : Boolean(instrumentName);

                                            const domainKey =
                                                typeof variableName === 'string' &&
                                                instrumentDomainRelation &&
                                                Object.prototype.hasOwnProperty.call(instrumentDomainRelation, variableName)
                                                    ? variableName
                                                    : table_name;
                                            const domain = domainKey ? instrumentDomainRelation?.[domainKey] : undefined;
                                            const unitLabel = domain === 'Tabular Imaging'
                                                ? 'Number of runs'
                                                : 'Number of participants';

                                            const tooltipParts: string[] = [
                                                `<div><strong>Variable:</strong> ${variableName}</div>`,
                                                `<div><strong>${unitLabel}:</strong> ${escapeHtml(value)}</div>`,
                                            ];

                                            if (hasInstrument) {
                                                tooltipParts.push(
                                                    `<div><strong>Instrument:</strong> ${escapeHtml(instrumentName as string)}</div>`
                                                );
                                            }

                                            return `<div style="${TOOLTIP_PADDING}">${tooltipParts.join('')}</div>`;
                                        },
                                    },
                                    itemStyler: (bar: AgBarSeriesItemStylerParams<any>) => {
                                        return {
                                            fill: getBarFill ? getBarFill(bar.datum, bar.fill) : bar.fill
                                        }
                                    },
                                    fill: colours.categorical.eight,
                                },
                            ],
                        }}
                    />
            }
        </ChartPaper>
    )
};

export default BarChart;
